import { useCallback } from "react";
import {
  filterOutByParticipantSid,
  hasParticipantBySid,
  remoteParticipantToDiagnosticInfo,
} from "../../video-sdk/core/utils";
import { RemoteParticipant } from "../../video-sdk/core/room/participant/RemoteParticipant";
import { useAtomCallback } from "jotai/utils";
import { useSetAtom } from "jotai";
import {
  participantsAtom,
  remoteParticipantConnectionStatesByIdAtom,
} from "../../video-sdk/core/room/participant/atoms";
import { useAppDependenciesContext } from "../../state";
import { TrackingEvents } from "../../events";
import { parseIdentity } from "../../video-sdk/core/room/participant/utils";
import { omit } from "remeda";
import { ParticipantConnectionState } from "../../video-sdk/core/room/participant/types";
import { secondsToMilliseconds } from "date-fns";
import { wait } from "../../utils";
import { roomEventListenerMutex } from "./useRoomEventListeners";
import { Mutex } from "async-mutex";

export const WAIT_TIME_FOR_RECONNECTING_USER_MS = secondsToMilliseconds(3);

/**
 * If the disconnected participant was previously in a reconnecting state, waits
 * for {@link WAIT_TIME_FOR_RECONNECTING_USER_MS} to allow them to reconnect
 * before cleaning up the participant from state. If the disconnected
 * participant was not reconnecting, then the operation is executed immediately.
 *
 * TODO: This is a temporary solution to briefly keep the last participant in
 * the UI while they are reconnecting, because the app immediately disconnects
 * otherwise. TREE-437 would ideally implement a proper UI state for when a user
 * becomes the sole participant that would replace this.
 */
async function waitForReconnectingParticipant({
  previousParticipantConnectionState,
  operation,
  eventHandlerMutex,
}: {
  previousParticipantConnectionState: ParticipantConnectionState;
  operation: () => void;
  eventHandlerMutex: Mutex;
}) {
  if (
    previousParticipantConnectionState ===
    ParticipantConnectionState.Reconnecting
  ) {
    // 🔓 Allow other event handlers to run while waiting
    eventHandlerMutex.release();
    return wait(WAIT_TIME_FOR_RECONNECTING_USER_MS).finally(async () =>
      // 🔒
      eventHandlerMutex.runExclusive(operation),
    );
  }

  operation();
}

/**
 * Handles when a remote participant is disconnected from the room i.e. no
 * longer in the room.
 */
export function useParticipantDisconnectedEventHandler() {
  const setParticipants = useSetAtom(participantsAtom);
  const getParticipants = useAtomCallback(
    useCallback((get) => get(participantsAtom), []),
  );
  const getRemoteParticipantConnectionStatesById = useAtomCallback(
    useCallback((get) => get(remoteParticipantConnectionStatesByIdAtom), []),
  );
  const setRemoteParticipantConnectionStatesById = useSetAtom(
    remoteParticipantConnectionStatesByIdAtom,
  );
  const { sendLoggingEvents, logger } = useAppDependenciesContext();

  return useCallback(
    async (
      participant: RemoteParticipant,
      {
        onParticipantDisconnect,
        eventHandlerMutex = roomEventListenerMutex,
      }: {
        onParticipantDisconnect?: (participant: RemoteParticipant) => void;
        /**
         * Dependency injection useful for testing. Defaults to
         * {@link roomEventListenerMutex}.
         */
        eventHandlerMutex?: Mutex;
      } = {},
    ) => {
      const hasParticipant = hasParticipantBySid(
        getParticipants(),
        participant,
      );

      // Ignore disconnect events for participant instances that are not
      // stored to avoid edge cases where a ghost disconnect event affects
      // rejoined participants. In general, a disconnect event for a
      // participant that the app is not tracking should be a no-op.
      if (!hasParticipant) {
        logger.warn(
          "Ignoring disconnect event for unknown participant",
          remoteParticipantToDiagnosticInfo(participant),
        );
        return;
      }

      sendLoggingEvents(
        TrackingEvents.REMOTE_PARTICIPANT_DISCONNECTED,
        {
          participant: remoteParticipantToDiagnosticInfo(participant),
        },
        { logLevel: "info", message: "Remote participant disconnected" },
      );

      const participantUserId = parseIdentity(participant.identity).id;
      const previousParticipantConnectionState =
        getRemoteParticipantConnectionStatesById()[participantUserId];

      await waitForReconnectingParticipant({
        previousParticipantConnectionState,
        eventHandlerMutex,
        operation() {
          setParticipants((prev) =>
            filterOutByParticipantSid(prev, participant.sid),
          );
          setRemoteParticipantConnectionStatesById((prev) =>
            omit(prev, [participantUserId]),
          );
          onParticipantDisconnect?.(participant);
        },
      });
    },
    [
      getParticipants,
      getRemoteParticipantConnectionStatesById,
      logger,
      sendLoggingEvents,
      setParticipants,
      setRemoteParticipantConnectionStatesById,
    ],
  );
}
